import React, { useEffect } from 'react';
import { Controller, type Path, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { z } from 'zod';

import type { PatientFieldOperation, PatientUpdatePayload } from '../../types';
import { PatientField } from '../../types';

// Zod schema for validation
const patientFieldOperationSchema = z.object({
  field: z.nativeEnum({} as Record<string, PatientField>),
  value: z.string().min(1, 'Value is required'),
  previous_value: z.string().nullable(),
  source_form_field_id: z.string(),
  confidence_score: z.number().min(0).max(1),
});

const patientUpdateSchema = z.object({
  patient_id: z.string(),
  form_session_id: z.string(),
  form_title: z.string(),
  submitted_at: z.string(),
  operations: z.array(patientFieldOperationSchema),
});

type FormData = z.infer<typeof patientUpdateSchema>;

interface PatientUpdateFormProps {
  payload: PatientUpdatePayload;
  onChange: (updatedPayload: PatientUpdatePayload) => void;
  readOnly?: boolean;
}

const getFieldDisplayName = (operation: PatientFieldOperation): string => {
  // Use field_display_name if available, otherwise fallback to formatted field name
  if (operation.field_display_name) {
    return operation.field_display_name;
  }

  const fieldNames: Record<PatientField, string> = {
    [PatientField.FIRST_NAME]: 'First Name',
    [PatientField.LAST_NAME]: 'Last Name',
    [PatientField.EMAIL]: 'Email',
    [PatientField.PHONE]: 'Phone',
    [PatientField.ADDRESS]: 'Address',
    [PatientField.DATE_OF_BIRTH]: 'Date of Birth',
    [PatientField.GENDER]: 'Gender',
    [PatientField.EMERGENCY_CONTACT_NAME]: 'Emergency Contact Name',
    [PatientField.EMERGENCY_CONTACT_PHONE]: 'Emergency Contact Phone',
    [PatientField.INSURANCE_MEMBER_ID]: 'Insurance Member ID',
    [PatientField.INSURANCE_GROUP_ID]: 'Insurance Group ID',
  };
  return fieldNames[operation.field] || operation.field;
};

export const PatientUpdateForm: React.FC<PatientUpdateFormProps> = ({
  payload,
  onChange,
  readOnly = false,
}) => {
  const { control, watch } = useForm<FormData>({
    resolver: zodResolver(patientUpdateSchema),
    defaultValues: payload,
  });

  // Watch for changes and call onChange
  useEffect(() => {
    const subscription = watch(value => {
      if (value.operations) {
        onChange(value as PatientUpdatePayload);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onChange]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review and edit the following patient information updates from{' '}
        <strong>{payload.form_title}</strong>:
      </Typography>

      <Stack spacing={2}>
        {payload.operations.map((operation, index) => {
          const fieldName = `operations.${String(index)}.value` as Path<FormData>;
          return (
            <Card key={`${operation.field}-${index}`} variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
                  {getFieldDisplayName(operation)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}
                    >
                      Current:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontStyle: operation.previous_value ? 'normal' : 'italic',
                        opacity: operation.previous_value ? 1 : 0.7,
                        fontSize: '0.875rem',
                        minHeight: '1.5em',
                      }}
                    >
                      {operation.previous_value || 'No current value'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}
                    >
                      New:
                    </Typography>
                    <Controller
                      name={fieldName}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          variant="outlined"
                          disabled={readOnly}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          multiline={operation.field === PatientField.ADDRESS}
                          rows={operation.field === PatientField.ADDRESS ? 2 : 1}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};
